/**
 * THE CROSS-FRAMEWORK CONTROL CATALOGUE, AND THE COVERAGE VIEW.
 *
 * Two screens in one file because they are one idea:
 *
 *   /catalogue        — the list of things you actually do, sorted by how much
 *                       leverage each one carries.
 *   /catalogue/:key   — one control: every framework requirement it covers,
 *                       with the citation and an honest FULL/PARTIAL verdict,
 *                       whether this organisation satisfies it, and the single
 *                       place its evidence lives.
 *
 * ---------------------------------------------------------------------------
 *  WHAT THIS SCREEN REFUSES TO SHOW
 * ---------------------------------------------------------------------------
 * The "Coverage" tab does show a per-framework percentage, but only as a plain
 * count ratio: requirements FULLY discharged by controls marked satisfied, over
 * the framework's requirement total (see CoverageView and getCoverage()). No
 * weights are invented and PARTIAL mappings are never added in — they are a
 * separate sentence. Everywhere else the counts are facts (how many
 * requirements, how many fully covered) and are never blended into a score.
 *
 * PARTIAL mappings are shown with the same prominence as FULL ones and their
 * reasons are rendered in full, not behind a tooltip. If that makes the screen
 * longer, the screen is longer.
 */

import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router-dom';
import {
  ORG_CONTROL_STATUSES,
  type ControlCoverage,
  type OrgControlStatus,
} from '@opencomply/shared';
import { api } from '../lib/api';
import { useAuth } from '../lib/auth';
import { useApiError } from '../lib/useApiError';
import { cn } from '../lib/cn';
import { Card, Pill, Skeleton, ErrorNotice, EmptyState, type Tone } from '../components/ui';
import { EvidencePanel } from '../components/EvidencePanel';
import { ReuseSummary } from '../components/EvidenceReuse';
import { MappingSources } from '../components/MappingSources';
import { CoverageView, COVERAGE_STRINGS } from '../components/CoverageView';

// ---------------------------------------------------------------------------
//  Strings — inline English defaults, as EVIDENCE_STRINGS does.
// ---------------------------------------------------------------------------

export const CATALOGUE_STRINGS = {
  'catalogue.title': 'Control catalogue',
  'catalogue.subtitle':
    'The things you actually do, mapped onto every framework requirement they bear on. Prove one of these and it counts everywhere it is mapped.',
  'catalogue.loadFailed': 'Could not load the control catalogue.',
  'catalogue.empty': 'The control catalogue has not been seeded yet.',
  'catalogue.emptyHint': 'Run the database seed to populate it.',

  // List
  'catalogue.filterDomain': 'Domain',
  'catalogue.filterAll': 'All domains',
  'catalogue.filterStatus': 'Status',
  'catalogue.filterAllStatuses': 'All statuses',
  'catalogue.sortLeverage': 'Most leverage first',
  'catalogue.sortDomain': 'By domain',
  'catalogue.coversFrameworks_one': 'Covers {{count}} framework',
  'catalogue.coversFrameworks_other': 'Covers {{count}} frameworks',
  'catalogue.requirements_one': '{{count}} requirement',
  'catalogue.requirements_other': '{{count}} requirements',
  'catalogue.fullPartial': '{{full}} full · {{partial}} partial',
  'catalogue.evidenceCount_one': '{{count}} item of evidence',
  'catalogue.evidenceCount_other': '{{count}} items of evidence',
  'catalogue.noEvidence': 'No evidence attached',
  'catalogue.showing': 'Showing {{shown}} of {{total}}',

  // Detail
  'catalogue.back': 'Back to the catalogue',
  'catalogue.whatProvesIt': 'What proves this control',
  'catalogue.coverage': 'What this covers',
  'catalogue.coverageHint':
    'Each line is a citation you can check against the published text. “Full” means operating this control discharges that requirement; “Partial” means it contributes but does not.',
  'catalogue.coverageFull': 'Full',
  'catalogue.coveragePartial': 'Partial',
  'catalogue.whyPartial': 'Why only partly:',
  'catalogue.unlicensed':
    'Not licensed for {{framework}} — the requirement text is hidden, but the citation is public and is shown.',
  'catalogue.yourPosition': 'Your position',
  'catalogue.status.NOT_ASSESSED': 'Not assessed yet',
  'catalogue.status.SATISFIED': 'Satisfied',
  'catalogue.status.PARTIAL': 'Partly satisfied',
  'catalogue.status.NOT_SATISFIED': 'Not satisfied',
  'catalogue.status.NOT_APPLICABLE': 'Not applicable',
  'catalogue.reviewedBy': 'Last reviewed {{date}} by {{name}}',
  'catalogue.reviewedOn': 'Last reviewed {{date}}',
  'catalogue.neverReviewed': 'Nobody has recorded a position on this control yet.',
  'catalogue.setStatus': 'Record your position',
  'catalogue.setStatusHint':
    'Recorded once for the whole organisation. It applies to every requirement listed below.',
  'catalogue.saveFailed': 'Could not save that. Try again.',
  'catalogue.satisfiedWithoutEvidence':
    'Marked satisfied with no evidence attached. Every requirement listed above rests on an artefact nobody has uploaded yet.',
  'catalogue.partialStillOpen':
    'This control is only partly satisfied, so the requirements it fully covers are still open.',
  'catalogue.notFound': 'That control is not in the catalogue.',
  'catalogue.evidence': 'Evidence',
  'catalogue.evidenceHint':
    'Attached here once, and it supports every requirement listed above.',
} as const;

export type CatalogueKey = keyof typeof CATALOGUE_STRINGS;

const PLURAL_KEYS = [
  'catalogue.coversFrameworks',
  'catalogue.requirements',
  'catalogue.evidenceCount',
] as const;
export type CataloguePluralKey = (typeof PLURAL_KEYS)[number];

const STATUS_TONE: Record<OrgControlStatus, Tone> = {
  NOT_ASSESSED: 'neutral',
  SATISFIED: 'good',
  PARTIAL: 'warning',
  NOT_SATISFIED: 'critical',
  NOT_APPLICABLE: 'neutral',
};

// ---------------------------------------------------------------------------
//  Transport types
// ---------------------------------------------------------------------------

interface CatalogueListItem {
  key: string;
  title: string;
  domain: string | null;
  frameworks: Array<{ key: string; name: string }>;
  frameworkCount: number;
  requirementCount: number;
  fullCount: number;
  partialCount: number;
  status: OrgControlStatus;
  reviewedAt: string | null;
  evidenceCount: number;
}

interface CatalogueListResponse {
  items: CatalogueListItem[];
  domains: string[];
}

interface CoverageRequirement {
  questionId: string;
  questionCode: string;
  questionText: string | null;
  requirementCode: string;
  ref: string;
  coverage: ControlCoverage;
  rationale: string | null;
}

interface CoverageFramework {
  frameworkKey: string;
  frameworkName: string;
  frameworkVersion: string;
  licensed: boolean;
  requirements: CoverageRequirement[];
}

interface CatalogueDetailResponse {
  control: {
    key: string;
    title: string;
    description: string | null;
    evidenceGuidance: string | null;
    domain: string | null;
  };
  frameworks: CoverageFramework[];
  totals: {
    frameworkCount: number;
    requirementCount: number;
    fullCount: number;
    partialCount: number;
  };
  adoption: {
    organizationControlId: string | null;
    status: OrgControlStatus;
    note: string | null;
    reviewedAt: string | null;
    reviewedBy: string | null;
    evidenceCount: number;
  };
}

// ---------------------------------------------------------------------------
//  Shared translation helpers
// ---------------------------------------------------------------------------

function useCatalogueStrings() {
  const { t, i18n } = useTranslation();
  const tx = (key: CatalogueKey, options?: Record<string, unknown>) =>
    t(key, { defaultValue: CATALOGUE_STRINGS[key], ...options });
  /** Plural-aware: i18next picks the suffix, we pick the inline fallback. */
  const txn = (key: CataloguePluralKey, count: number) =>
    t(key, {
      count,
      defaultValue: CATALOGUE_STRINGS[(count === 1 ? `${key}_one` : `${key}_other`) as CatalogueKey],
    });
  const formatDate = (iso: string) =>
    new Intl.DateTimeFormat(i18n.language, { dateStyle: 'medium' }).format(new Date(iso));
  return { tx, txn, formatDate };
}

// ===========================================================================
//  LIST
// ===========================================================================

function ControlListView() {
  const { tx, txn } = useCatalogueStrings();
  const [domain, setDomain] = useState<string>('');
  const [status, setStatus] = useState<string>('');

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['catalogue', 'list'],
    queryFn: () => api.get<CatalogueListResponse>('/catalogue'),
  });

  const shown = useMemo(() => {
    const items = data?.items ?? [];
    return items
      .filter((i) => (domain ? i.domain === domain : true))
      .filter((i) => (status ? i.status === status : true))
      // Most leverage first: the control that buys the most coverage is the
      // one worth doing next, which is the only ordering that answers the
      // question a compliance lead actually brings to this screen.
      .sort(
        (a, b) =>
          b.frameworkCount - a.frameworkCount || b.requirementCount - a.requirementCount,
      );
  }, [data?.items, domain, status]);

  if (isLoading) {
    return (
      <div className="space-y-4">
        {[0, 1, 2, 3].map((i) => <Skeleton key={i} className="h-24" />)}
      </div>
    );
  }

  if (isError) {
    return (
      <ErrorNotice message={tx('catalogue.loadFailed')} onRetry={() => void refetch()} />
    );
  }

  return (
    <div className="space-y-5">
      {(data?.items.length ?? 0) === 0 ? (
        <EmptyState title={tx('catalogue.empty')} description={tx('catalogue.emptyHint')} />
      ) : (
        <>
          <div className="flex flex-wrap items-end gap-3">
            <label className="flex flex-col gap-1">
              <span className="oc-label">{tx('catalogue.filterDomain')}</span>
              <select
                className="oc-input"
                value={domain}
                onChange={(e) => setDomain(e.target.value)}
              >
                <option value="">{tx('catalogue.filterAll')}</option>
                {data?.domains.map((d) => <option key={d} value={d}>{d}</option>)}
              </select>
            </label>
            <label className="flex flex-col gap-1">
              <span className="oc-label">{tx('catalogue.filterStatus')}</span>
              <select
                className="oc-input"
                value={status}
                onChange={(e) => setStatus(e.target.value)}
              >
                <option value="">{tx('catalogue.filterAllStatuses')}</option>
                {ORG_CONTROL_STATUSES.map((s) => (
                  <option key={s} value={s}>{tx(`catalogue.status.${s}`)}</option>
                ))}
              </select>
            </label>
            <p className="oc-muted text-xs pb-2">
              {tx('catalogue.showing', { shown: shown.length, total: data?.items.length ?? 0 })}
            </p>
          </div>

          <ul className="space-y-3">
            {shown.map((item) => (
              <li key={item.key}>
                <Card className="p-4">
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div className="min-w-0 space-y-1">
                      <Link
                        to={`/catalogue/${encodeURIComponent(item.key)}`}
                        className="text-sm font-semibold text-ink hover:text-brand hover:underline"
                      >
                        {item.title}
                      </Link>
                      <p className="text-xs text-ink-muted">
                        {item.domain && <span>{item.domain} · </span>}
                        <span className="font-mono">{item.key}</span>
                      </p>
                      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-ink-muted">
                        <span className="font-medium text-ink">
                          {txn('catalogue.coversFrameworks', item.frameworkCount)}
                        </span>
                        <span aria-hidden>·</span>
                        <span>{txn('catalogue.requirements', item.requirementCount)}</span>
                        <span aria-hidden>·</span>
                        {/*
                          Full and partial are reported side by side and never
                          summed. "19 requirements" alone would read as 19
                          discharged, which for a control with 8 partials it is
                          not.
                        */}
                        <span>
                          {tx('catalogue.fullPartial', {
                            full: item.fullCount,
                            partial: item.partialCount,
                          })}
                        </span>
                      </div>
                      <div className="flex flex-wrap gap-1.5 pt-0.5">
                        {item.frameworks.map((fw) => (
                          <span key={fw.key} className="oc-pill-neutral text-xs">{fw.name}</span>
                        ))}
                      </div>
                    </div>

                    <div className="flex flex-col items-end gap-1.5 shrink-0">
                      <Pill tone={STATUS_TONE[item.status]}>
                        {tx(`catalogue.status.${item.status}`)}
                      </Pill>
                      <span className="text-xs text-ink-muted">
                        {item.evidenceCount > 0
                          ? txn('catalogue.evidenceCount', item.evidenceCount)
                          : tx('catalogue.noEvidence')}
                      </span>
                    </div>
                  </div>
                </Card>
              </li>
            ))}
          </ul>
        </>
      )}
    </div>
  );
}

export function ControlCataloguePage() {
  const { tx } = useCatalogueStrings();
  const { t } = useTranslation();
  const [view, setView] = useState<'coverage' | 'controls'>('coverage');

  const tabs: Array<{ id: 'coverage' | 'controls'; label: string }> = [
    { id: 'coverage', label: t('coverage.tab.coverage', { defaultValue: COVERAGE_STRINGS['coverage.tab.coverage'] }) },
    { id: 'controls', label: t('coverage.tab.controls', { defaultValue: COVERAGE_STRINGS['coverage.tab.controls'] }) },
  ];

  return (
    <div className="oc-page space-y-5 animate-fade-in">
      <header>
        <h1 className="oc-h1">{tx('catalogue.title')}</h1>
        <p className="oc-muted mt-1 max-w-3xl">{tx('catalogue.subtitle')}</p>
      </header>

      <div role="tablist" className="inline-flex rounded-lg bg-ink/[0.06] p-1 gap-1">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            type="button"
            role="tab"
            aria-selected={view === tab.id}
            onClick={() => setView(tab.id)}
            className={cn(
              'min-h-[40px] px-4 rounded-md text-sm font-medium transition-colors',
              view === tab.id ? 'bg-surface text-ink shadow-sm' : 'text-ink-secondary hover:text-ink',
            )}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {view === 'coverage' ? (
        <>
          <CoverageView />
          {/* What the organisation has ACTUALLY got out of the catalogue: counts of
              proven controls and the re-proofs avoided. Same story as the bars
              above, in the units of work saved rather than share of a framework. */}
          <ReuseSummary />
        </>
      ) : null}

      {/* Where our mappings come from and what they were compared with. CC BY 4.0
          needs the credit to be VISIBLE, and it is a selling point besides. */}
      <MappingSources />

      {view === 'controls' ? <ControlListView /> : null}
    </div>
  );
}

// ===========================================================================
//  COVERAGE VIEW
// ===========================================================================

export function ControlCoveragePage() {
  const { key = '' } = useParams<{ key: string }>();
  const { tx, txn, formatDate } = useCatalogueStrings();
  const { allows } = useAuth();
  const describeApiError = useApiError();
  const queryClient = useQueryClient();
  const [saveError, setSaveError] = useState<string | null>(null);

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['catalogue', 'detail', key],
    queryFn: () => api.get<CatalogueDetailResponse>(`/catalogue/${encodeURIComponent(key)}`),
    enabled: !!key,
  });

  const save = useMutation({
    mutationFn: (status: OrgControlStatus) =>
      api.patch(`/catalogue/${encodeURIComponent(key)}/status`, { status }),
    onSuccess: () => {
      setSaveError(null);
      // Both this screen and the list (status column) are now stale. Also the
      // leverage panels elsewhere, which read the same adoption row.
      void queryClient.invalidateQueries({ queryKey: ['catalogue'] });
    },
    onError: (err) => setSaveError(describeApiError(err) || tx('catalogue.saveFailed')),
  });

  if (isLoading) {
    return (
      <div className="oc-page space-y-4">
        <Skeleton className="h-9 w-72" />
        <Skeleton className="h-40" />
      </div>
    );
  }

  if (isError || !data) {
    return (
      <div className="oc-page space-y-4">
        <Link to="/catalogue" className="text-sm text-brand hover:underline">
          {tx('catalogue.back')}
        </Link>
        <ErrorNotice message={tx('catalogue.notFound')} onRetry={() => void refetch()} />
      </div>
    );
  }

  const { control, frameworks, totals, adoption } = data;
  const hasFullMapping = totals.fullCount > 0;

  return (
    <div className="oc-page space-y-5 animate-fade-in">
      <div>
        <Link to="/catalogue" className="text-sm text-brand hover:underline">
          {tx('catalogue.back')}
        </Link>
      </div>

      <header className="space-y-1">
        <h1 className="oc-h1">{control.title}</h1>
        <p className="text-xs text-ink-muted">
          {control.domain && <span>{control.domain} · </span>}
          <span className="font-mono">{control.key}</span>
        </p>
        {control.description && <p className="oc-muted max-w-3xl pt-1">{control.description}</p>}
      </header>

      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-ink-muted">
        <span className="font-medium text-ink">
          {txn('catalogue.coversFrameworks', totals.frameworkCount)}
        </span>
        <span aria-hidden>·</span>
        <span>{txn('catalogue.requirements', totals.requirementCount)}</span>
        <span aria-hidden>·</span>
        <span>
          {tx('catalogue.fullPartial', { full: totals.fullCount, partial: totals.partialCount })}
        </span>
      </div>

      {/* --- Your position ------------------------------------------------- */}
      <Card className="p-4 space-y-2">
        <h2 className="text-sm font-semibold text-ink">{tx('catalogue.yourPosition')}</h2>
        <div className="flex flex-wrap items-center gap-3">
          <Pill tone={STATUS_TONE[adoption.status]}>
            {tx(`catalogue.status.${adoption.status}`)}
          </Pill>
          <span className="text-xs text-ink-muted">
            {adoption.evidenceCount > 0
              ? txn('catalogue.evidenceCount', adoption.evidenceCount)
              : tx('catalogue.noEvidence')}
          </span>
        </div>

        {adoption.note && <p className="text-sm text-ink-secondary">{adoption.note}</p>}

        <p className="text-xs text-ink-muted">
          {adoption.reviewedAt
            ? adoption.reviewedBy
              ? tx('catalogue.reviewedBy', {
                  date: formatDate(adoption.reviewedAt),
                  name: adoption.reviewedBy,
                })
              : tx('catalogue.reviewedOn', { date: formatDate(adoption.reviewedAt) })
            : tx('catalogue.neverReviewed')}
        </p>

        {/*
          The two statements this screen must never let a reader miss. A green
          "Satisfied" with nothing attached, or a PARTIAL control sitting above
          a list of FULL mappings, are both ways a customer walks into an audit
          believing something that is not true.
        */}
        {adoption.status === 'SATISFIED' && adoption.evidenceCount === 0 && (
          <p className="text-xs text-warning bg-warning/10 rounded-md p-2">
            {tx('catalogue.satisfiedWithoutEvidence')}
          </p>
        )}
        {adoption.status === 'PARTIAL' && hasFullMapping && (
          <p className="text-xs text-warning bg-warning/10 rounded-md p-2">
            {tx('catalogue.partialStillOpen')}
          </p>
        )}

        {allows('control.evaluate') && (
          <fieldset className="space-y-1.5 pt-1">
            <legend className="oc-label">{tx('catalogue.setStatus')}</legend>
            <p className="text-xs text-ink-muted">{tx('catalogue.setStatusHint')}</p>
            <div className="flex flex-wrap gap-1.5">
              {ORG_CONTROL_STATUSES.map((value) => (
                <button
                  key={value}
                  type="button"
                  aria-pressed={adoption.status === value}
                  disabled={save.isPending}
                  onClick={() => save.mutate(value)}
                  className={cn(
                    'oc-btn-ghost oc-btn-sm',
                    adoption.status === value && 'ring-2 ring-brand font-semibold',
                  )}
                >
                  {tx(`catalogue.status.${value}`)}
                </button>
              ))}
            </div>
            {saveError && <p className="text-xs text-critical">{saveError}</p>}
          </fieldset>
        )}
      </Card>

      {/* --- What proves it ------------------------------------------------ */}
      {control.evidenceGuidance && (
        <Card className="p-4 space-y-1.5">
          <h2 className="text-sm font-semibold text-ink">{tx('catalogue.whatProvesIt')}</h2>
          <p className="text-sm text-ink-secondary">{control.evidenceGuidance}</p>
        </Card>
      )}

      {/* --- Coverage ------------------------------------------------------- */}
      <section className="space-y-3">
        <div>
          <h2 className="oc-h2">{tx('catalogue.coverage')}</h2>
          <p className="oc-muted text-xs mt-1 max-w-3xl">{tx('catalogue.coverageHint')}</p>
        </div>

        {frameworks.map((fw) => (
          <Card key={fw.frameworkKey} className="p-4 space-y-2">
            <div className="flex flex-wrap items-baseline justify-between gap-2">
              <h3 className="text-sm font-semibold text-ink">
                {fw.frameworkName}{' '}
                <span className="font-normal text-ink-muted">{fw.frameworkVersion}</span>
              </h3>
              <span className="text-xs text-ink-muted">
                {txn('catalogue.requirements', fw.requirements.length)}
              </span>
            </div>

            {!fw.licensed && (
              <p className="text-xs text-ink-muted bg-surface-sunken rounded-md p-2">
                {tx('catalogue.unlicensed', { framework: fw.frameworkName })}
              </p>
            )}

            <ul className="divide-y divide-line">
              {fw.requirements.map((req) => (
                <li key={`${req.questionId}`} className="py-2 space-y-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-sm font-medium text-ink">{req.ref}</span>
                    <Pill tone={req.coverage === 'FULL' ? 'good' : 'warning'}>
                      {req.coverage === 'FULL'
                        ? tx('catalogue.coverageFull')
                        : tx('catalogue.coveragePartial')}
                    </Pill>
                    <span className="text-xs text-ink-muted font-mono">{req.questionCode}</span>
                    {req.requirementCode && req.requirementCode !== req.ref && (
                      <span className="text-xs text-ink-muted">({req.requirementCode})</span>
                    )}
                  </div>
                  {req.questionText && (
                    <p className="text-sm text-ink-secondary">{req.questionText}</p>
                  )}
                  {req.coverage === 'PARTIAL' && req.rationale && (
                    <p className={cn('text-xs text-ink-muted')}>
                      <span className="font-medium text-ink">{tx('catalogue.whyPartial')}</span>{' '}
                      {req.rationale}
                    </p>
                  )}
                </li>
              ))}
            </ul>
          </Card>
        ))}
      </section>

      {/* --- Evidence ------------------------------------------------------- */}
      {adoption.organizationControlId && (
        <Card className="p-4 space-y-2">
          <div>
            <h2 className="text-sm font-semibold text-ink">{tx('catalogue.evidence')}</h2>
            <p className="text-xs text-ink-muted mt-0.5">{tx('catalogue.evidenceHint')}</p>
          </div>
          <EvidencePanel organizationControlId={adoption.organizationControlId} />
        </Card>
      )}
    </div>
  );
}
